import clsx from 'clsx';
import Fuse from 'fuse.js';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { resolveURLToNavigation } from '@gitbook/app-navigation';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Collection,
    CollectionExpandButton,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    IsolateField,
    TextInput,
    Tree,
    TreeItem,
    TreeItemContent,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { SpaceIcon } from './SpaceIcon';

interface SpaceTreeItem {
    type: 'space';
    id: string;
    space: api.Space;
}

interface CollectionTreeItem {
    type: 'collection';
    id: string;
    title: string;
    children: SpaceTreeItem[];
}

type HierarchicalTreeItem = CollectionTreeItem | SpaceTreeItem;

export interface SpaceListSelectorProps {
    spaces: api.Space[];
    collectionNameMap: Map<string, string>;
    onChange: (item: api.Space) => void;
    autoFocus?: boolean;
    actionLabel: string;
}

export function SpaceListSelector({
    spaces,
    onChange,
    autoFocus,
    actionLabel,
    collectionNameMap,
}: SpaceListSelectorProps) {
    const sortedSpaces = React.useMemo(() => {
        return spaces.sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
    }, [spaces]);

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const [searchQuery, setSearchQuery] = React.useState('');
    const [userExpandedKeys, setUserExpandedKeys] = React.useState<Set<string>>(new Set());

    // Create hierarchical tree structure
    const hierarchicalItems = React.useMemo(() => {
        return createHierarchicalSpaceTree(sortedSpaces, collectionNameMap);
    }, [sortedSpaces, collectionNameMap]);

    const fuse = React.useMemo(() => {
        return new Fuse(sortedSpaces, {
            shouldSort: true,
            threshold: 0.6,
            distance: 100,
            minMatchCharLength: 1,
            keys: ['title'],
        });
    }, [sortedSpaces]);

    // Filter based on search query
    const filteredHierarchicalItems = React.useMemo(
        () => filterHierarchicalTree(hierarchicalItems, searchQuery, fuse),
        [hierarchicalItems, searchQuery, fuse]
    );

    const currentItems = searchQuery ? filteredHierarchicalItems : hierarchicalItems;

    // Auto-expand collections that have spaces (initial state)
    const autoExpandedKeys = React.useMemo(() => {
        return new Set(
            currentItems
                .filter(
                    (item): item is CollectionTreeItem =>
                        item.type === 'collection' && item.children.length > 0
                )
                .map((item) => item.id)
        );
    }, [currentItems]);

    // Determine final expanded keys based on user interaction
    const currentExpandedKeys = React.useMemo(() => {
        // During normal browsing, use user preferences but default to auto-expanded
        // If user hasn't interacted with a collection, it should be expanded by default
        const expanded = new Set(autoExpandedKeys);

        userExpandedKeys.forEach((key) => {
            if (autoExpandedKeys.has(key)) {
                expanded.delete(key);
            } else {
                expanded.add(key);
            }
        });

        return expanded;
    }, [autoExpandedKeys, userExpandedKeys]);

    const handleExpandedChange = (keys: Set<string>) => {
        const userToggled = new Set<string>();

        autoExpandedKeys.forEach((key) => {
            if (!keys.has(key)) {
                userToggled.add(key);
            }
        });

        keys.forEach((key) => {
            if (!autoExpandedKeys.has(key)) {
                userToggled.add(key);
            }
        });

        setUserExpandedKeys(userToggled);
    };

    return (
        <>
            {/* Enable search only if there are more than 7 spaces (some are not visible) */}
            {spaces.length > 7 ? (
                <IsolateField>
                    <TextInput
                        ref={inputRef}
                        placeholder="Find space..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                        }}
                        leadingIcon={Icon.Search}
                        className="mb-2"
                        autoFocus={autoFocus}
                    />
                </IsolateField>
            ) : null}

            <div>
                <Tree
                    items={currentItems}
                    expandedKeys={currentExpandedKeys}
                    onExpandedChange={handleExpandedChange}
                    aria-label="Spaces"
                    className="flex h-fit max-h-80 flex-col gap-0.5 overflow-y-auto border-0 px-0"
                    renderEmptyState={() => {
                        return (
                            <EmptyState className="h-80">
                                <EmptyStateIcon>
                                    <Icon.Space />
                                </EmptyStateIcon>
                                <Heading>
                                    {searchQuery ? 'No space matching' : 'No spaces available'}
                                </Heading>
                                <Description>
                                    {searchQuery
                                        ? 'There are no spaces matching your search query.'
                                        : 'There are no spaces to display.'}
                                </Description>
                            </EmptyState>
                        );
                    }}
                >
                    {function renderTreeItem(item) {
                        switch (item.type) {
                            case 'space': {
                                return (
                                    <TreeItem
                                        textValue={item.space.title}
                                        className={clsx(
                                            'group/item relative cursor-default rounded-md transition-all hover:bg-standout-base'
                                        )}
                                    >
                                        <SpaceTreeItem
                                            space={item.space}
                                            actionLabel={actionLabel}
                                            onSelect={() => {
                                                onChange(item.space);
                                            }}
                                        />
                                    </TreeItem>
                                );
                            }
                            case 'collection': {
                                return (
                                    <TreeItem
                                        textValue={item.title}
                                        className={clsx(
                                            'group/tree-item relative cursor-default rounded-md transition-all'
                                        )}
                                    >
                                        <TreeItemContent>
                                            <CollectionExpandButton />
                                            <div className="flex-1 truncate text-left font-medium">
                                                {item.title}
                                            </div>
                                            <div className="text-muted text-sm">
                                                {item.children.length === 1 ? 'space' : 'spaces'}
                                            </div>
                                        </TreeItemContent>
                                        <Collection items={item.children}>
                                            {renderTreeItem}
                                        </Collection>
                                    </TreeItem>
                                );
                            }
                            default: {
                                assertNever(item);
                            }
                        }
                    }}
                </Tree>
            </div>
        </>
    );
}

function SpaceTreeItem({
    actionLabel,
    space,
    onSelect,
}: {
    space: api.Space;
    actionLabel: string;
    onSelect: () => void;
}) {
    const screen = resolveURLToNavigation(space.urls.app);
    assert(screen !== null, 'Space URL should resolve to a navigation screen');

    return (
        <TreeItemContent>
            <SpaceIcon space={space} size={20} />
            <div className="flex-1 truncate text-left">{space.title}</div>
            <div className="text-muted group-hover/item:invisible">
                Last edited <DateShortRelative value={new Date(space.updatedAt)} onlyDate={false} />
            </div>
            <div className="invisible absolute right-1.5 flex shrink-0 items-center gap-2 group-hover/item:visible">
                <Button
                    href={{ screen }}
                    target="_blank"
                    label="View space"
                    trailingIcon={Icon.ArrowUpRight}
                    size="small"
                    kind="secondary"
                />
                <Button
                    label={actionLabel}
                    size="small"
                    kind="primary"
                    onPress={() => onSelect()}
                />
            </div>
        </TreeItemContent>
    );
}

function createHierarchicalSpaceTree(
    spaces: api.Space[],
    collectionNameMap: Map<string, string>
): ReadonlyArray<HierarchicalTreeItem> {
    const result: HierarchicalTreeItem[] = [];

    // Group spaces by their parent collection
    const spacesByCollection = new Map<string | null, api.Space[]>();
    spaces.forEach((space) => {
        const parentId = space.parent ?? null;
        if (!spacesByCollection.has(parentId)) {
            spacesByCollection.set(parentId, []);
        }

        spacesByCollection.get(parentId)?.push(space);
    });

    // Add collections with their spaces
    spacesByCollection.forEach((spacesInCollection, collectionId) => {
        if (collectionId && spacesInCollection.length > 0) {
            result.push({
                id: collectionId,
                type: 'collection',
                title: collectionNameMap.get(collectionId) ?? `Collection ${collectionId}`,
                children: spacesInCollection.map((space) => ({
                    type: 'space',
                    id: space.id,
                    space,
                })),
            });
        }
    });

    // Add root-level spaces
    const rootSpaces = spacesByCollection.get(null) ?? [];
    rootSpaces.forEach((space) => {
        result.push({ type: 'space', id: space.id, space });
    });

    return result;
}

function filterHierarchicalTree(
    items: ReadonlyArray<HierarchicalTreeItem>,
    searchQuery: string,
    fuse: Fuse<api.Space>
): ReadonlyArray<HierarchicalTreeItem> {
    if (!searchQuery.trim()) {
        return items;
    }

    const filteredItems: HierarchicalTreeItem[] = [];
    const matchingSpaceIds = new Set(fuse.search(searchQuery).map(({ item }) => item.id));

    items.forEach((item) => {
        if (item.type === 'collection') {
            // Filter children spaces that match the search
            const matchingChildren = item.children.filter(({ space }) =>
                matchingSpaceIds.has(space.id)
            );

            // Include collection if it has matching children
            if (matchingChildren.length > 0) {
                filteredItems.push({ ...item, children: matchingChildren });
            }
        } else {
            // Include root-level space if it matches
            if (matchingSpaceIds.has(item.space.id)) {
                filteredItems.push(item);
            }
        }
    });

    return filteredItems;
}
